import {
  DEFAULT_SIDE_BRANDING,
  type JoinRole,
  type SideBrandingSet,
} from '@badger-sentinel/protocol';

import { SLOT } from '../theme/imagery.js';

/**
 * The three sides, as the entry and seat-select mockups draw them.
 *
 * Split in two, because the two halves have different owners:
 *
 *   - the **chrome** — accent colour and crest slot — is the design's, fixed for
 *     the life of the client. Blue is the friendly side and red is the hostile
 *     one on every screen in the app, and a scenario that could repaint that
 *     would be a scenario that could make its own map unreadable;
 *   - the **words** — what the side is called and whose command it is — are the
 *     scenario's (§10.2's `sides` block), because the pairing is. `First
 *     Contact` fields U.S. Indo-Pacific Command; the next scenario will not, and
 *     it must not need a separate client build to say so.
 *
 * `DEFAULT_SIDE_BRANDING` is the protocol's so that the server and this file
 * cannot disagree about the fallback. Entry draws it up to the passphrase (it
 * cannot know the scenario before then — §3.3), and swaps to the game's own the
 * moment the join response lands. That swap is deliberate and it is why the
 * handoff crest reads from the response rather than from the constant: the
 * closing beat of entry settles this cluster into the exact spot seat select's
 * header occupies, and two different names either side of the cut is a jump.
 *
 * Role keys are the protocol's (`zJoinRole`) — a GM reads a side's password out
 * of a briefing and it says REDFOR, so the password prompt says REDFOR whatever
 * the scenario calls the side.
 */

export interface SideDef {
  readonly key: JoinRole;
  readonly name: string;
  readonly identity: string;
  readonly accent: string;
  readonly crestSlot: string;
}

interface SideChrome {
  readonly accent: string;
  readonly crestSlot: string;
}

const CHROME: Record<JoinRole, SideChrome> = {
  BLUFOR: { accent: 'var(--blue)', crestSlot: SLOT.sideCrest('BLUFOR') },
  REDFOR: { accent: 'var(--red)', crestSlot: SLOT.sideCrest('REDFOR') },
  GM: { accent: 'var(--primary)', crestSlot: SLOT.sideCrest('GM') },
};

/** Card order on the side-select screen. */
export const SIDE_ORDER: readonly JoinRole[] = ['BLUFOR', 'REDFOR', 'GM'];

export function sidesFrom(branding: SideBrandingSet = DEFAULT_SIDE_BRANDING): readonly SideDef[] {
  return SIDE_ORDER.map((key) => sideOf(key, branding));
}

export function sideOf(role: JoinRole, branding: SideBrandingSet = DEFAULT_SIDE_BRANDING): SideDef {
  const words = branding[role];
  return { key: role, name: words.name, identity: words.identity, ...CHROME[role] };
}

/** The generic set, for the screens that run before there is a game to ask. */
export const SIDES: readonly SideDef[] = sidesFrom();
